import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuthStore } from "./store";
import { registerServiceWorker, subscribeToPush } from "./utils/pushNotifications";
import api from "./api";
import PopupNotifications from "./components/PopupNotifications";
import SmartsuppWidget from "./components/SmartsuppWidget";

// Layouts
import MainLayout from "./layouts/MainLayout";
import AdminLayout from "./layouts/AdminLayout";
import AuthLayout from "./layouts/AuthLayout";

// Pages
import HomePage from "./pages/HomePage";
import ProductPage from "./pages/ProductPage";
import CartPage from "./pages/CartPage";
import OrdersPage from "./pages/OrdersPage";
import OrderDetailPage from "./pages/OrderDetailPage";
import DepositsPage from "./pages/DepositsPage";
import TransfersPage from "./pages/TransfersPage";
import ProfilePage from "./pages/ProfilePage";
import NotificationsPage from "./pages/NotificationsPage";
import DisputesPage from "./pages/DisputesPage";
import DisputeDetailPage from "./pages/DisputeDetailPage";

// Auth Pages
import LoginPage from "./pages/auth/LoginPage";
import RegisterPage from "./pages/auth/RegisterPage";

// Admin Pages
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminProducts from "./pages/admin/AdminProducts";
import AdminOrders from "./pages/admin/AdminOrders";
import AdminDeposits from "./pages/admin/AdminDeposits";
import AdminDisputes from "./pages/admin/AdminDisputes";
import AdminChat from "./pages/admin/AdminChat";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminNotifications from "./pages/admin/AdminNotifications";
import AdminAIAssistant from "./pages/admin/AdminAIAssistant";
import AdminOutOfStock from "./pages/admin/AdminOutOfStock";
import AdminVIP from "./pages/admin/AdminVIP";

// User Pages
import VIPPage from "./pages/VIPPage";

// Store pages
import StorePage from "./pages/StorePage";

// Protected Route Component
const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const { isAuthenticated, user, isHydrated } = useAuthStore();
  
  // Show loading while hydrating from storage
  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }
  
  if (requireAdmin && !user?.is_admin) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

// Public Route (redirect to home if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, isHydrated } = useAuthStore();
  
  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="w-8 h-8 border-2 border-[#FFD700] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }
  
  if (isAuthenticated) {
    return <Navigate to="/" replace />;
  }
  
  return children;
};

function App() {
  const { fetchUser, setHydrated, isAuthenticated, user } = useAuthStore();
  
  useEffect(() => {
    // Mark as hydrated once zustand persist has loaded
    const unsubscribe = useAuthStore.persist.onFinishHydration(() => {
      setHydrated(true);
      // Fetch fresh user data if we have a token
      const token = localStorage.getItem('token');
      if (token) {
        fetchUser();
      }
    });
    
    // If already hydrated (fast reload)
    if (useAuthStore.persist.hasHydrated()) {
      setHydrated(true);
      const token = localStorage.getItem('token');
      if (token) {
        fetchUser();
      }
    }
    
    return unsubscribe;
  }, [fetchUser, setHydrated]);
  
  // Register service worker and subscribe to push notifications
  useEffect(() => {
    registerServiceWorker();
  }, []);
  
  // Subscribe to push when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      subscribeToPush(api).catch(console.error);
    }
  }, [isAuthenticated]);
  
  return (
    <BrowserRouter>
      <Toaster 
        position="top-right" 
        richColors 
        theme="dark"
        toastOptions={{
          style: {
            background: '#0A0A0A',
            border: '1px solid rgba(255, 255, 255, 0.1)',
          },
        }}
      />
      <PopupNotifications />
      {/* Smartsupp Live Chat Widget - for visitors & customers (admins reply from the Smartsupp dashboard) */}
      {!user?.is_admin && <SmartsuppWidget user={isAuthenticated ? user : null} />}
      <Routes>
        {/* Auth Routes */}
        <Route path="/login" element={
          <PublicRoute>
            <AuthLayout>
              <LoginPage />
            </AuthLayout>
          </PublicRoute>
        } />
        <Route path="/register" element={
          <PublicRoute>
            <AuthLayout>
              <RegisterPage />
            </AuthLayout>
          </PublicRoute>
        } />
        
        {/* Main App Routes */}
        <Route path="/" element={
          <ProtectedRoute>
            <MainLayout>
              <HomePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/products/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <ProductPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/cart" element={
          <ProtectedRoute>
            <MainLayout>
              <CartPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/orders" element={
          <ProtectedRoute>
            <MainLayout>
              <OrdersPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/orders/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <OrderDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/deposits" element={
          <ProtectedRoute>
            <MainLayout>
              <DepositsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/transfers" element={
          <ProtectedRoute>
            <MainLayout>
              <TransfersPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/profile" element={
          <ProtectedRoute>
            <MainLayout>
              <ProfilePage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/notifications" element={
          <ProtectedRoute>
            <MainLayout>
              <NotificationsPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/disputes" element={
          <ProtectedRoute>
            <MainLayout>
              <DisputesPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        <Route path="/disputes/:id" element={
          <ProtectedRoute>
            <MainLayout>
              <DisputeDetailPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        
        {/* Admin Routes */}
        <Route path="/admin" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDashboard />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/users" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminUsers />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/products" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminProducts />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/orders" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminOrders />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/deposits" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDeposits />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/disputes" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminDisputes />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/chat" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminChat />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/settings" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminSettings />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/notifications" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminNotifications />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/ai-assistant" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminAIAssistant />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/stock" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminOutOfStock />
            </AdminLayout>
          </ProtectedRoute>
        } />
        <Route path="/admin/vip" element={
          <ProtectedRoute requireAdmin>
            <AdminLayout>
              <AdminVIP />
            </AdminLayout>
          </ProtectedRoute>
        } />
        
        {/* VIP Page for Users */}
        <Route path="/vip" element={
          <ProtectedRoute>
            <MainLayout>
              <VIPPage />
            </MainLayout>
          </ProtectedRoute>
        } />
        
        {/* Public Reseller Store (no auth required) */}
        <Route path="/store/:domain" element={<StorePage />} />
        
        {/* Fallback */}
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
